import isEmpty from 'lodash/isEmpty'
import * as React from 'react'
import { Mutation, MutationFunction } from 'react-apollo'

import Form, { FormGroup, FormItem } from 'main/components/common/Form'
import { UserFields } from 'main/gql/user'
import useSSOContext from 'main/hooks/useSSOContext'
import {
  Button,
  colors,
  PhoneTextField,
  PostalCodeTextField,
  spacing,
  Text,
  TextField,
} from 'main/ui'

import {
  SUBMIT_FINAL_PARENT_SIGN_UP_FORM_MUTATION,
  SubmitFinalParentSignUpForm,
  SubmitFinalParentSignUpFormVariables,
} from './gql'
import { IParams, IValidation } from './types'
import { initialParams, validate } from './util'

interface IProps {
  cta?: string
  label?: string
  user: UserFields
  displayPhoneField?: boolean
  onComplete: () => void
  isOnboarding?: boolean
}

export default function FinalParentSignUpForm(props: IProps) {
  const {
    cta = 'Done',
    label = 'Finalize your account',
    user,
    displayPhoneField = true,
    onComplete,
    isOnboarding,
  } = props

  const { ssoContext } = useSSOContext()
  const savedForm = ssoContext?.signupData

  const initParams = Object.assign(
    initialParams(user),
    isOnboarding && !!savedForm
      ? {
          firstName: savedForm.firstName || '',
          lastName: savedForm.lastName || '',
          email: savedForm.email || '',
          phone: savedForm.phone || '',
        }
      : {},
  )

  const [params, setParams] = React.useState<IParams>(initParams)
  const [validation, setValidation] = React.useState<IValidation>({})
  const [error, setError] = React.useState<boolean>(false)
  const onChange = (changes: Partial<IParams>) => handleParamsChange(changes, setParams)

  return (
    <Mutation<SubmitFinalParentSignUpForm, SubmitFinalParentSignUpFormVariables>
      mutation={SUBMIT_FINAL_PARENT_SIGN_UP_FORM_MUTATION}
      onCompleted={() => onComplete()}
      onError={() => setError(true)}
    >
      {(mutate, { loading }) => (
        <Form onSubmit={() => handleSubmit(params, setValidation, setError, mutate)}>
          <FormItem label={label}>
            <div css={{ marginBottom: spacing.majorScale(2) }}>
              <FormGroup>
                <TextField
                  label="First name"
                  autoComplete="given-name"
                  placeholder="First name"
                  value={params.firstName}
                  onChange={firstName => onChange({ firstName })}
                  validationHint={validation.firstName}
                  required
                  disabled={isOnboarding && !!savedForm?.firstName}
                />
                <TextField
                  label="Last name"
                  autoComplete="family-name"
                  placeholder="Last name"
                  value={params.lastName}
                  onChange={lastName => onChange({ lastName })}
                  disabled={isOnboarding && !!savedForm?.lastName}
                />
              </FormGroup>
            </div>

            <div css={{ marginBottom: spacing.majorScale(error ? 2 : 4) }}>
              <FormGroup>
                {displayPhoneField && (
                  <PhoneTextField
                    label="Phone number"
                    placeholder="Phone number"
                    value={params.phone}
                    onChange={phone => onChange({ phone })}
                    validationHint={validation.phone}
                    disabled={isOnboarding && !!savedForm?.phone}
                  />
                )}
                <PostalCodeTextField
                  label="Zip code"
                  placeholder="Zip code"
                  value={params.postalCode}
                  onChange={postalCode => onChange({ postalCode })}
                  validationHint={validation.postalCode}
                />
                {!displayPhoneField && <div />}
              </FormGroup>
            </div>
          </FormItem>

          {error && (
            <div css={{ marginBottom: spacing.majorScale(2) }}>
              <Text css={{ color: colors.text.error }}>
                Something went wrong! Please try again.
              </Text>
            </div>
          )}

          <Button kind="regular" disabled={loading} full>
            {loading ? 'Submitting...' : cta}
          </Button>
        </Form>
      )}
    </Mutation>
  )
}

function handleParamsChange(
  changes: Partial<IParams>,
  setParams: React.Dispatch<React.SetStateAction<IParams>>,
) {
  setParams(params => ({ ...params, ...changes }))
}

function handleSubmit(
  params: IParams,
  setValidation: (validation: IValidation) => void,
  setError: (error: boolean) => void,
  mutate: MutationFunction<SubmitFinalParentSignUpForm, SubmitFinalParentSignUpFormVariables>,
) {
  const validation = validate(params)
  setError(false)
  setValidation(validation)
  if (!isEmpty(validation)) {
    return
  }

  const base = {
    firstName: params.firstName.trim(),
    lastName: params.lastName.trim(),
    phone: params.phone.trim(),
  }
  const user = params.postalCode
    ? { ...base, address: { postalCode: params.postalCode.trim() } }
    : base

  mutate({
    variables: {
      user,
    },
  })
}
